// ─── clipMotion ────────────────────────────────────────────────────────────
// Animaciones de entrada / salida que se aplican al *wrapper* de un clip
// (texto o template) sin tocar el contenido interno. El editor las elige
// vía dos dropdowns por clip; el renderer compone el estilo resultante
// con el resto del transform (posición, escala, etc.) del wrapper.
//
// Convención de progreso:
//   • Para `enter`: progress va de 0 (recién aparecido, estado "off") a 1
//     (estado neutro / steady) en el primer `enterDurationSec` del clip.
//   • Para `exit`: va de 0 (steady) a 1 (estado "off") en los últimos
//     `exitDurationSec` segundos del clip.
//
// Cada preset puede declarar:
//   - `enter(p)` / `exit(p)`: estilo aplicado al wrapper.
//   - `splitMode: 'chars' | 'words'` + `enterItem(p,i,n)` / `exitItem`:
//     los renderers de texto distribuyen el contenido en spans y aplican
//     este estilo por carácter o palabra, lo que habilita efectos
//     escalonados tipo cascade / typewriter.
//
// Mantenemos esto deliberadamente liviano: las transiciones son funciones
// puras de progreso, sin frame-rate ni timing imperativo. El playhead
// global del editor decide dónde estamos en la línea de tiempo.

import type React from 'react';

// ── Easing helpers ──────────────────────────────────────────────────────────
// Curvas baratitas que evitan importar una lib. Suficientes para UI motion.
const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);
const easeInCubic = (p: number) => p * p * p;
const easeOutBack = (p: number) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
};

// `clamp01` aparece en suficientes presets como para extraerlo.
const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n);

export type ClipMotionPreset = {
  id: string;
  label: string;
  /** Categoría: 'common' aparece en todos los clips; 'text' solo en los de texto. */
  scope: 'common' | 'text';
  /** Se aplica al wrapper. Devuelve `{}` si el preset solo opera por item. */
  enter?: (p: number) => React.CSSProperties;
  exit?: (p: number) => React.CSSProperties;
  /** Si está seteado, el renderer de texto va a partir el contenido en items. */
  splitMode?: 'chars' | 'words';
  enterItem?: (p: number, index: number, total: number) => React.CSSProperties;
  exitItem?: (p: number, index: number, total: number) => React.CSSProperties;
};

// ── Helpers de progreso por item ────────────────────────────────────────────
// Distribuimos el progreso global `p` entre `n` items con un solapamiento
// fijo, de modo que cada item tenga su propia ventana 0..1 dentro del
// progreso global. `overlap` controla cuán encimados arrancan los items
// (0 = uno tras otro sin overlap; 0.5 = el siguiente arranca cuando el
// anterior va por la mitad).
const itemProgress = (p: number, i: number, n: number, overlap = 0.6) => {
  if (n <= 1) return clamp01(p);
  const span = 1 / (n - (n - 1) * overlap);
  const start = i * span * (1 - overlap);
  const local = (p - start) / span;
  return clamp01(local);
};

// ── Presets ────────────────────────────────────────────────────────────────
// Para los enter, p=0 es "off" y p=1 es "neutro". Para los exit, p=0 es
// "neutro" y p=1 es "off". Los valores intermedios deben ser continuos
// para que no haya saltos al entrar/salir de la ventana.

const fade: ClipMotionPreset = {
  id: 'fade',
  label: 'Fundido',
  scope: 'common',
  enter: p => ({ opacity: easeOutCubic(p) }),
  exit: p => ({ opacity: 1 - easeInCubic(p) }),
};

// Slide presets — la dirección indica desde/hacia dónde se mueve el
// elemento. `slide-up` enter = entra deslizándose hacia arriba (viene de
// abajo); `slide-up` exit = sale hacia arriba.
const slideOffset = 40; // px; suficiente para que el efecto sea claro sin
                        // sacar el elemento muy lejos.

const slideUp: ClipMotionPreset = {
  id: 'slide-up',
  label: 'Slide arriba',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, transform: `translateY(${(1 - e) * slideOffset}px)` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `translateY(${-e * slideOffset}px)` };
  },
};

const slideDown: ClipMotionPreset = {
  id: 'slide-down',
  label: 'Slide abajo',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, transform: `translateY(${-(1 - e) * slideOffset}px)` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `translateY(${e * slideOffset}px)` };
  },
};

const slideLeft: ClipMotionPreset = {
  id: 'slide-left',
  label: 'Slide izquierda',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, transform: `translateX(${(1 - e) * slideOffset}px)` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `translateX(${-e * slideOffset}px)` };
  },
};

const slideRight: ClipMotionPreset = {
  id: 'slide-right',
  label: 'Slide derecha',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, transform: `translateX(${-(1 - e) * slideOffset}px)` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `translateX(${e * slideOffset}px)` };
  },
};

const scaleIn: ClipMotionPreset = {
  id: 'scale',
  label: 'Escalar',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, transform: `scale(${0.7 + 0.3 * e})` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `scale(${1 - 0.3 * e})` };
  },
};

const pop: ClipMotionPreset = {
  id: 'pop',
  label: 'Pop',
  scope: 'common',
  enter: p => {
    // Pop usa easeOutBack para overshoot: arranca chiquito, pasa de
    // 1 y baja. Más expresivo que un scale lineal.
    const e = easeOutBack(p);
    return { opacity: clamp01(p * 1.5), transform: `scale(${0.4 + 0.6 * e})` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, transform: `scale(${1 + 0.2 * e})` };
  },
};

const blur: ClipMotionPreset = {
  id: 'blur',
  label: 'Desenfoque',
  scope: 'common',
  enter: p => {
    const e = easeOutCubic(p);
    return { opacity: e, filter: `blur(${(1 - e) * 16}px)` };
  },
  exit: p => {
    const e = easeInCubic(p);
    return { opacity: 1 - e, filter: `blur(${e * 16}px)` };
  },
};

// ── Text-only presets ──────────────────────────────────────────────────────

// Typewriter: revela un carácter a la vez. Sin animación de transform por
// item — solo opacity 0/1 controlada por la ventana del item. Se ve como
// una máquina de escribir clásica.
const typewriter: ClipMotionPreset = {
  id: 'typewriter',
  label: 'Máquina de escribir',
  scope: 'text',
  splitMode: 'chars',
  enterItem: (p, i, n) => {
    // Cada char se ilumina cuando el progreso global cruzó su umbral.
    // Sin fade — el efecto es binario para que la cadencia se sienta
    // "tipográfica". Espacios incluidos (los muestra el wrapper, igual).
    const threshold = (i + 1) / n;
    return { opacity: p >= threshold ? 1 : 0 };
  },
  exitItem: (p, i, n) => {
    // En la salida desaparece de derecha a izquierda (igual de natural
    // que el revés del typewriter).
    const threshold = 1 - (i + 1) / n;
    return { opacity: p < threshold ? 1 : 0 };
  },
};

// Cascade por carácter: cada char hace fade + ligero translateY como si
// fuera "lloviendo" letras. El overlap genera la sensación de cascada.
const charCascade: ClipMotionPreset = {
  id: 'char-cascade',
  label: 'Cascada por letra',
  scope: 'text',
  splitMode: 'chars',
  enterItem: (p, i, n) => {
    const local = itemProgress(p, i, n, 0.7);
    const e = easeOutCubic(local);
    return { display: 'inline-block', opacity: e, transform: `translateY(${(1 - e) * 14}px)` };
  },
  exitItem: (p, i, n) => {
    const local = itemProgress(p, i, n, 0.7);
    const e = easeInCubic(local);
    return { display: 'inline-block', opacity: 1 - e, transform: `translateY(${-e * 14}px)` };
  },
};

// Cascade por palabra: idéntico al anterior pero a nivel palabra. Se
// percibe más "rítmico" para frases largas (ideal para títulos / claims).
const wordCascade: ClipMotionPreset = {
  id: 'word-cascade',
  label: 'Cascada por palabra',
  scope: 'text',
  splitMode: 'words',
  enterItem: (p, i, n) => {
    const local = itemProgress(p, i, n, 0.6);
    const e = easeOutCubic(local);
    return { display: 'inline-block', opacity: e, transform: `translateY(${(1 - e) * 18}px)` };
  },
  exitItem: (p, i, n) => {
    const local = itemProgress(p, i, n, 0.6);
    const e = easeInCubic(local);
    return { display: 'inline-block', opacity: 1 - e, transform: `translateY(${-e * 18}px)` };
  },
};

// ── Registries ─────────────────────────────────────────────────────────────
// Listas exportadas en el orden en que aparecen en los dropdowns. La
// primera entrada SIEMPRE es "Sin animación" para que el default sea
// trivial de seleccionar. El "none" no está en `CLIP_MOTION_PRESETS`
// porque los lookups por id devuelven `undefined` para los ids no
// reconocidos, que el renderer interpreta como "sin efecto".

export const CLIP_MOTION_PRESETS: ClipMotionPreset[] = [
  fade,
  slideUp,
  slideDown,
  slideLeft,
  slideRight,
  scaleIn,
  pop,
  blur,
  typewriter,
  charCascade,
  wordCascade,
];

const PRESET_BY_ID = new Map(CLIP_MOTION_PRESETS.map(p => [p.id, p]));

export const getMotionPreset = (id: string | undefined): ClipMotionPreset | undefined =>
  id ? PRESET_BY_ID.get(id) : undefined;

/** Helper de UI: opciones para el `<select>` (incluye "none"). */
export const motionOptionsFor = (scope: 'text' | 'common'): Array<{ id: string; label: string }> => {
  const list: Array<{ id: string; label: string }> = [{ id: 'none', label: 'Sin animación' }];
  for (const p of CLIP_MOTION_PRESETS) {
    if (scope === 'common' && p.scope === 'text') continue;
    list.push({ id: p.id, label: p.label });
  }
  return list;
};

// ── Wrapper-level styles ───────────────────────────────────────────────────
// Devuelve el estilo combinado de enter+exit para el wrapper en un instante
// dado. `localTime` es segundos desde el inicio del clip; `duration` es la
// duración total del clip; `enterId` / `exitId` los presets seleccionados;
// `enterDur` / `exitDur` las longitudes (en seg) de las ventanas de
// entrada y salida.
//
// Notas:
//  • Si las ventanas se solapan (clips muy cortos), priorizamos el exit
//    apenas arranca, porque visualmente "salir" es más informativo que
//    "completar la entrada".
//  • Si el preset es per-item, el wrapper queda sin estilo extra; es el
//    renderer de texto el que mapea por items.
export type ClipMotionState = {
  /** Estilo a aplicar al wrapper. Vacío si no hay efecto. */
  wrapperStyle: React.CSSProperties;
  /** Si hay un preset per-item activo, info para el renderer de texto. */
  itemPreset?: {
    splitMode: 'chars' | 'words';
    /** Estilo por item (carácter / palabra). Compuesto enter+exit. */
    styleAt: (index: number, total: number) => React.CSSProperties;
  };
};

export function computeClipMotion(opts: {
  localTime: number;
  duration: number;
  enterId?: string;
  exitId?: string;
  enterDur?: number;
  exitDur?: number;
}): ClipMotionState {
  const { localTime, duration } = opts;
  const enterPreset = getMotionPreset(opts.enterId);
  const exitPreset = getMotionPreset(opts.exitId);
  const enterDur = Math.max(0, opts.enterDur ?? 0.5);
  const exitDur = Math.max(0, opts.exitDur ?? 0.5);

  // Progreso enter/exit; ambos pueden estar activos a la vez (clip corto
  // en el que se solapan; el resultado se compone en el wrapper sumando
  // transforms y multiplicando opacities).
  const enterP = enterPreset && enterDur > 0
    ? clamp01(localTime / enterDur)
    : 1; // sin enter ⇒ asumimos steady inmediato
  const exitP = exitPreset && exitDur > 0
    ? clamp01((localTime - (duration - exitDur)) / exitDur)
    : 0; // sin exit ⇒ nunca empezó

  // ── Wrapper style ────────────────────────────────────────────────
  let wrapperStyle: React.CSSProperties = {};
  // Aplicamos enter (a menos que sea preset per-item: ahí solo opera el
  // itemStyle, el wrapper queda neutro).
  if (enterPreset?.enter && !enterPreset.splitMode) {
    wrapperStyle = { ...wrapperStyle, ...enterPreset.enter(enterP) };
  }
  // El exit se compone *encima*: si está activo, multiplica opacity y
  // suma su transform al de enter. Para mantenerlo simple, sobreescribimos
  // si entra en juego (en la práctica enter ya está completo cuando
  // arranca el exit, salvo clips ultracortos donde aceptamos el snap).
  if (exitPreset?.exit && !exitPreset.splitMode && exitP > 0) {
    const exitStyle = exitPreset.exit(exitP);
    wrapperStyle = mergeMotionStyles(wrapperStyle, exitStyle);
  }

  // ── Per-item ────────────────────────────────────────────────────
  // Si alguno de los dos presets es per-item, exponemos un styleAt(i,n).
  const itemEnter = enterPreset?.splitMode ? enterPreset : undefined;
  const itemExit = exitPreset?.splitMode ? exitPreset : undefined;
  const splitMode = (itemEnter?.splitMode || itemExit?.splitMode) as
    | 'chars'
    | 'words'
    | undefined;
  let itemPreset: ClipMotionState['itemPreset'];
  if (splitMode) {
    itemPreset = {
      splitMode,
      styleAt: (index, total) => {
        let style: React.CSSProperties = {};
        if (itemEnter?.enterItem) {
          style = { ...style, ...itemEnter.enterItem(enterP, index, total) };
        }
        if (itemExit?.exitItem && exitP > 0) {
          const exitStyle = itemExit.exitItem(exitP, index, total);
          style = mergeMotionStyles(style, exitStyle);
        }
        return style;
      },
    };
  }

  return { wrapperStyle, itemPreset };
}

// Compone dos estilos de motion. Suma transforms (concatenando strings) y
// multiplica opacidades. El resto de las props se sobreescribe — está OK
// porque los presets que comparten props (filter, etc.) en general no se
// usan combinados.
function mergeMotionStyles(
  a: React.CSSProperties,
  b: React.CSSProperties,
): React.CSSProperties {
  const out: React.CSSProperties = { ...a, ...b };
  if (a.transform && b.transform) {
    out.transform = `${a.transform} ${b.transform}`;
  }
  if (typeof a.opacity === 'number' && typeof b.opacity === 'number') {
    out.opacity = a.opacity * b.opacity;
  }
  if (a.filter && b.filter) {
    // CSS permite encadenar filtros con espacio.
    out.filter = `${a.filter} ${b.filter}`;
  }
  return out;
}

/** Splitea un string en items respetando el splitMode. Para chars
 *  preservamos los espacios como items propios para que el wrap natural
 *  funcione; para words devolvemos pares "palabra + espacio siguiente"
 *  (excepto la última) para que cada palabra arrastre su separador. */
export function splitTextForMotion(
  text: string,
  mode: 'chars' | 'words',
): string[] {
  if (mode === 'chars') {
    // Array.from respeta surrogate pairs (emojis, etc.) — Necesario si
    // alguna vez el usuario escribe emojis en un texto.
    return Array.from(text);
  }
  // Words: split por whitespace conservando los espacios pegados al item
  // anterior. Esto evita líneas raras al wrap-ear porque los browsers
  // permiten quebrar entre items, no dentro.
  const out: string[] = [];
  const parts = text.split(/(\s+)/); // intercala palabras y espacios
  let buffer = '';
  for (const p of parts) {
    if (/^\s+$/.test(p)) {
      // Espacios: pegar al ultimo item para que no exista un item-vacío.
      if (buffer) {
        out.push(buffer + p);
        buffer = '';
      } else if (out.length > 0) {
        out[out.length - 1] += p;
      }
    } else if (p.length > 0) {
      if (buffer) out.push(buffer);
      buffer = p;
    }
  }
  if (buffer) out.push(buffer);
  return out;
}
